/**
 * VibrancyDashboardService — FRONTEND DATA SERVICE
 */
import { axiosClient } from '../../../library/Service/axiosClient';

export type InstituteType = 'k12' | 'college_ug' | 'college_pg';
export type DashboardVersion = 'sm' | 'client';
export type Period = 'monthly' | 'quarterly' | 'halfyearly' | 'annual';

const wrap = <T>(data: T) => Promise.resolve({ data: { success: true, data } });

// ── Period labels (mirrors prototype) ──────────────────────────────────────
export const PERIOD_LABELS: Record<Period, string> = {
    monthly: 'Apr 2026',
    quarterly: 'Q1 2026 (Jan–Mar)',
    halfyearly: 'H1 2026 (Jan–Jun)',
    annual: 'Year 2026',
};

export const PERIOD_WORD: Record<Period, string> = {
    monthly: 'Month',
    quarterly: 'Quarter',
    halfyearly: 'Half-Year',
    annual: 'Year',
};

export const COMPARE_OPTIONS: Record<Period, string[]> = {
    monthly: [],
    quarterly: ['Q4 2025 (Oct–Dec)', 'Q1 2025 (Same Qtr Last Yr)'],
    halfyearly: ['H2 2025 (Jul–Dec)', 'H1 2025 (Same Half Last Yr)'],
    annual: ['Year 2025', 'Year 2024'],
};

/**
 * Descriptor of the selected institute. For the SM version this is sourced from
 * the INSTITUTE_DOMAIN domain-data category (code/value + flexCol); for the
 * client version it comes from the login context. `type` drives the academic
 * footprint; `studentModuleActive` / `counsellingActive` are package flags.
 */
export interface InstituteDescriptor {
    id: string | number;
    name: string;
    type: InstituteType;
    studentModuleActive?: boolean;
    counsellingActive?: boolean;
    /** client-version campuses; empty => single-campus (no campus dropdown) */
    campuses?: { id: number; name: string }[];
}

const INTERNAL_ADMIN_BASE = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/vibrancy`;

export const VibrancyDashboardService = {
    /** Resolves the logged-in institute admin's institute from the backend. */
    getClientInstitute: () => {
        let instituteId: string | number | undefined;
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            // Management/institute admins store their institute as primaryInstitute
            instituteId = user?.primaryInstitute || user?.instituteId;
        } catch (_) { /* localStorage unavailable */ }
        return axiosClient().get<{ success: boolean; data: InstituteDescriptor }>(
            `${INTERNAL_ADMIN_BASE()}/client-institute`,
            { params: { institute_id: instituteId } }
        );
    },

    /**
     * Returns every dashboard block for the given institute + period.
     * Dummy: the dataset is largely period-invariant; only labels/headline
     * change with period. `version` affects highlight attribution + watchlist links.
     */
    getDashboardData: (institute: InstituteDescriptor, period: Period, version: DashboardVersion) =>
        wrap(buildDashboard(institute, period, version)),
};

// ── Dashboard payload builder ───────────────────────────────────────────────
function buildDashboard(inst: InstituteDescriptor, period: Period, version: DashboardVersion) {
    const periodLabel = PERIOD_LABELS[period];
    // Package flags: respect explicit values; otherwise default by institute type
    // (K-12 institutes get student + counselling modules; colleges don't).
    const studentModuleActive = inst.studentModuleActive ?? (inst.type === 'k12');
    const counsellingActive = inst.counsellingActive ?? (inst.type === 'k12');
    return {
        institute: {
            id: inst.id, name: inst.name, type: inst.type,
            studentModuleActive,
            counsellingActive,
        },
        periodLabel,

        // VD-02 Highlight of the Period
        highlight: {
            published: true,
            type: 'alumni' as 'alumni' | 'outreach' | 'engagement' | 'news',
            icon: '🎓',
            headline: 'Pearl Jimmy Mistry (Class of 2013) appointed Director at Della Group',
            body: 'Pearl completed her Masters in Management from London Business School and has risen to Director level at Della Group, specialising in Luxury Design & Retail. She is among 3 alumni featured in our "Leaders of the Future" spotlight this month.',
            sourceNote: version === 'client'
                ? `Curated by Univariety · ${periodLabel}`
                : 'Source: Alumni Profile · AI Generated',
            chips: ['1,248 vibrancy pts', '3 contributions', 'WEF spotlight'],
        },

        // VD-03 Vibrancy Points
        topOverall: [
            { rank: 1, initials: 'PM', name: 'Pearl Jimmy Mistry', sub: 'Class of 2013 · Director @ Della Group', score: 1248, avatarBg: '#fef3c7', avatarColor: '#92400e' },
            { rank: 2, initials: 'BD', name: 'Brinda Didwania', sub: 'Class of 2019 · Senior Analyst @ Redseer', score: 1104, avatarBg: '#d1fae5', avatarColor: '#065f46' },
            { rank: 3, initials: 'AD', name: 'Ameya Davda', sub: 'Class of 2008 · Partner @ Space Valwt', score: 987, avatarBg: '#cffafe', avatarColor: '#0891b2' },
            { rank: 4, initials: 'RK', name: 'Rajath K K', sub: 'Class of 2018 · Software Engineer', score: 843, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
            { rank: 5, initials: 'SB', name: 'Sanjana Balivada', sub: 'Class of 2022 · Founder @ Girl Up Discover', score: 712, avatarBg: '#ede9fe', avatarColor: '#7c3aed' },
        ],
        biggestGains: [
            { initials: 'AD', name: 'Ameya Davda', sub: 'Class of 2008 · 3 contributions + commitment', delta: 148, avatarBg: '#cffafe', avatarColor: '#0891b2' },
            { initials: 'RK', name: 'Rajath K K', sub: 'Class of 2018 · Event + Q&A + video watches', delta: 112, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
            { initials: 'SB', name: 'Sanjana Balivada', sub: 'Class of 2022 · Guidance video + event', delta: 89, avatarBg: '#ede9fe', avatarColor: '#7c3aed' },
            { initials: 'NK', name: 'Neeraj Kapoor', sub: 'Class of 2020 · Profile updated + outreach response', delta: 54, avatarBg: '#fef3c7', avatarColor: '#92400e' },
            { initials: 'MR', name: 'Meera Rajan', sub: 'Class of 2021 · Campaign click + login', delta: 18, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
        ],

        // VD-04 Watchlist
        signals: {
            total: 14, prevTotal: 9, signalLayer: 6, strategicLayer: 8,
            alumni: [
                { initials: 'MR', name: 'Meera Rajan', layer: 'Strategic', batch: 'Class of 2021', reason: 'Career move detected', score: 712, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
                { initials: 'PV', name: 'Priya Venkat', layer: 'Signal', batch: 'Class of 2019', reason: 'Engagement surge detected', score: 638, avatarBg: '#fef3c7', avatarColor: '#92400e' },
                { initials: 'AS', name: 'Arjun Shah', layer: 'Strategic', batch: 'Class of 2016', reason: 'Prestigious institution join', score: 594, avatarBg: '#d1fae5', avatarColor: '#065f46' },
                { initials: 'KN', name: 'Kavya Nair', layer: 'Signal', batch: 'Class of 2022', reason: 'Profile enrichment spike', score: 481, avatarBg: '#cffafe', avatarColor: '#0891b2' },
                { initials: 'DM', name: 'Dev Malhotra', layer: 'Strategic', batch: 'Class of 2017', reason: 'Location shift to Mumbai', score: 443, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
            ],
        },
        advancements: {
            total: 7, prevTotal: 4,
            stages: [
                { label: 'Identified→Validated', count: 3 },
                { label: 'Validated→Warmed', count: 2 },
                { label: 'Warmed→Engaged', count: 2 },
            ],
            qualifiedContributing: 0,
            alumni: [
                { initials: 'AD', name: 'Ameya Davda', batch: 'Class of 2008', from: 'Identified', to: 'Validated', score: 987, avatarBg: '#cffafe', avatarColor: '#0891b2' },
                { initials: 'NK', name: 'Neeraj Kapoor', batch: 'Class of 2020', from: 'Validated', to: 'Warmed', score: 843, avatarBg: '#fef3c7', avatarColor: '#92400e' },
                { initials: 'RK', name: 'Rajath K K', batch: 'Class of 2018', from: 'Identified', to: 'Validated', score: 843, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
                { initials: 'MR', name: 'Meera Rajan', batch: 'Class of 2021', from: 'Warmed', to: 'Engaged', score: 712, avatarBg: '#ebf2ff', avatarColor: '#1b52c4' },
                { initials: 'AS', name: 'Arjun Shah', batch: 'Class of 2016', from: 'Warmed', to: 'Engaged', score: 594, avatarBg: '#d1fae5', avatarColor: '#065f46' },
            ],
        },

        // VD-05 Profile Overview Hero Strip
        profileOverview: {
            totalAlumni: 428,
            metrics: [
                { label: 'Total Alumni', value: '428', sub: '↑ 8 this period', color: '#0a1f44', prev: '420', delta: '↑ +8' },
                { label: 'Active Engagement', value: '86%', sub: '↑ 4% vs prior', color: '#1b52c4', prev: '82%', delta: '↑ +4pp' },
                { label: 'Profile Completeness', value: '71%', sub: '↑ 3% avg score', color: '#0f9d58', prev: '68%', delta: '↑ +3pp' },
                { label: 'Working Alumni', value: '42', sub: '12% of total', color: '#f59e0b', prev: '38', delta: '↑ +4' },
                { label: 'Pursuing Studies', value: '54%', sub: '231 alumni', color: '#0891b2', prev: '52%', delta: '↑ +2pp' },
                { label: 'Prestigious Colleges', value: '97', sub: 'IITs · IIMs · UCL · Toronto', color: '#7c3aed', prev: '91', delta: '↑ +6' },
            ],
        },

        // VD-06 Batch Bifurcation
        batch: {
            totalProfiled: 335,
            programs: inst.type === 'k12'
                ? [
                    { value: 'all', label: 'All Programs' },
                    { value: 'science', label: 'Science (PCM/PCB)' },
                    { value: 'commerce', label: 'Commerce' },
                    { value: 'arts', label: 'Arts / Humanities' },
                    { value: 'ib', label: 'IB Diploma' },
                    { value: 'igcse', label: 'IGCSE' },
                ]
                : [
                    { value: 'all', label: 'All Programs' },
                    { value: 'ug', label: 'UG Programs' },
                    { value: 'pg', label: 'PG Programs' },
                ],
            data: {
                all: [
                    { year: 2026, size: 5, be: 1, fe: 0 }, { year: 2025, size: 68, be: 52, fe: 34 },
                    { year: 2024, size: 72, be: 3, fe: 1 }, { year: 2023, size: 65, be: 18, fe: 9 },
                    { year: 2022, size: 70, be: 43, fe: 28 }, { year: 2021, size: 80, be: 64, fe: 47 },
                    { year: 2020, size: 74, be: 50, fe: 31 }, { year: 2019, size: 68, be: 27, fe: 14 },
                    { year: 2018, size: 62, be: 32, fe: 18 }, { year: 2017, size: 58, be: 16, fe: 8 },
                    { year: 2016, size: 60, be: 19, fe: 9 }, { year: 2015, size: 55, be: 8, fe: 3 },
                ],
                science: [{ year: 2025, size: 28, be: 22, fe: 14 }, { year: 2024, size: 30, be: 2, fe: 1 }, { year: 2023, size: 26, be: 8, fe: 4 }, { year: 2022, size: 29, be: 18, fe: 12 }, { year: 2021, size: 32, be: 26, fe: 19 }],
                commerce: [{ year: 2025, size: 24, be: 19, fe: 12 }, { year: 2024, size: 26, be: 1, fe: 0 }, { year: 2023, size: 22, be: 7, fe: 3 }, { year: 2022, size: 25, be: 15, fe: 10 }],
                arts: [{ year: 2025, size: 16, be: 11, fe: 8 }, { year: 2024, size: 16, be: 0, fe: 0 }, { year: 2023, size: 17, be: 3, fe: 2 }],
                ib: [{ year: 2025, size: 14, be: 8, fe: 6 }, { year: 2024, size: 12, be: 0, fe: 0 }],
                igcse: [{ year: 2025, size: 10, be: 3, fe: 3 }, { year: 2024, size: 8, be: 0, fe: 0 }],
                ug: [{ year: 2025, size: 60, be: 44, fe: 30 }, { year: 2024, size: 58, be: 12, fe: 6 }, { year: 2023, size: 55, be: 30, fe: 18 }],
                pg: [{ year: 2025, size: 20, be: 14, fe: 9 }, { year: 2024, size: 18, be: 4, fe: 2 }],
            } as Record<string, { year: number; size: number; be: number; fe: number }[]>,
        },

        // VD-07 India vs Abroad + Student Coverage
        location: { indiaPct: 82, abroadPct: 18, abroadCount: 58, prevAbroadPct: 15, abroadDelta: '↑ +3pp' },
        studentCoverage: studentModuleActive
            ? { activeStudents: 1240, profileComplete: 64 }
            : null,

        // VD-08 Academic Footprint
        academic: {
            topUgCourses: [
                { label: 'Business Management', pct: 16.4, color: '#1b52c4', width: 100 },
                { label: 'Engineering & Technology', pct: 10.3, color: '#0891b2', width: 63 },
                { label: 'Medicine', pct: 8.6, color: '#dc3545', width: 52 },
                { label: 'Economics & Commerce', pct: 8.1, color: '#f59e0b', width: 49 },
                { label: 'CS & IT', pct: 4.2, color: '#7c3aed', width: 25 },
            ],
            ugIndianColleges: [
                { name: 'Vellore Institute of Technology', count: 38 },
                { name: 'SRM Institute of Science & Tech', count: 24 },
                { name: 'CHRIST (Deemed University)', count: 19 },
                { name: "St. Joseph's College, Devagiri", count: 14 },
                { name: 'Manipal Institute of Technology', count: 12 },
            ],
            pgIndianColleges: [
                { name: 'IIM Ahmedabad', count: 6 },
                { name: 'NIT Calicut', count: 5 },
                { name: 'ICAI', count: 4 },
            ],
            ugIntlColleges: [
                { name: 'University College London', count: 8 },
                { name: 'University of Toronto', count: 6 },
                { name: 'Cardiff University', count: 5 },
            ],
            pgIntlColleges: [
                { name: 'London Business School', count: 4 },
                { name: 'LSE', count: 3 },
                { name: "Taylor's University", count: 2 },
            ],
            prestigious: {
                total: 97,
                breakdown: [
                    { label: 'IITs / NITs', count: 44, color: '#212529', width: 45 },
                    { label: 'IIMs / Top B-Schools', count: 24, color: '#1b52c4', width: 24 },
                    { label: 'Ivy / Oxbridge / UCL', count: 29, color: '#198754', width: 29 },
                ],
                prevTotal: 91, delta: '↑ +6',
            },
            scholarships: {
                total: 18,
                breakdown: [
                    { label: 'Merit-based', count: 12, color: '#198754', width: 67 },
                    { label: 'Need-based', count: 4, color: '#1b52c4', width: 22 },
                    { label: 'Sport / Arts', count: 2, color: '#7c3aed', width: 11 },
                ],
            },
            pgResearch: {
                total: 12,
                breakdown: [
                    { label: 'PhD / Doctoral', count: 7, color: '#1b52c4', width: 58 },
                    { label: 'Research Fellowships', count: 5, color: '#0891b2', width: 42 },
                ],
            },
        },

        // VD-09 Notable Alumni
        notableAlumni: [
            { initials: 'PM', name: 'Pearl Jimmy Mistry', meta: 'Class of 2013 · Mumbai', role: '💼 Director @ Della Group', edu: '🎓 London Business School · Masters in Mgmt', tags: [{ text: 'Luxury & Design', cls: 'bg-primary bg-opacity-10 text-primary-emphasis' }, { text: 'Leaders of Future', cls: 'bg-success bg-opacity-10 text-success-emphasis' }], avatarBg: '#fef3c7', avatarColor: '#92400e' },
            { initials: 'BD', name: 'Brinda Didwania', meta: 'Class of 2019 · DPS Megacity', role: '💼 Senior Analyst @ Redseer Strategy', edu: '🌍 WEF Global Shaper · SSA Kathmandu', tags: [{ text: 'Youngest WEF Curator', cls: 'bg-bs-purple bg-opacity-10 text-dark-emphasis' }, { text: 'Global Recognition', cls: 'bg-warning bg-opacity-10 text-warning-emphasis' }], avatarBg: '#d1fae5', avatarColor: '#065f46' },
            { initials: 'AD', name: 'Ameya Davda', meta: 'Class of 2008 · Mumbai', role: '💼 Partner @ Space Valwt', edu: '🎓 University of San Francisco · Business Mgmt', tags: [{ text: 'Storage & Warehousing', cls: 'bg-primary bg-opacity-10 text-primary-emphasis' }, { text: 'Active Mentor', cls: 'bg-success bg-opacity-10 text-success-emphasis' }], avatarBg: '#cffafe', avatarColor: '#0891b2' },
        ],

        // VD-10 Outreach
        outreach: {
            summary: {
                totalAttempts: 30, prevTotalAttempts: 24,
                coverage: 100, successful: 21, prevSuccessful: 16, followUp: 9,
                disposition: { successful: 70, followUp: 30, noResponse: 0 },
                purposes: [
                    { label: 'Profile Updation', count: 4, color: '#1b52c4', width: 70 },
                    { label: 'Event Invite', count: 4, color: '#0891b2', width: 40 },
                    { label: 'Driving Pledge', count: 3, color: '#f59e0b', width: 30 },
                    { label: 'Feature Consent', count: 3, color: '#7c3aed', width: 30 },
                ],
            },
            channels: [
                { label: '🤖 AI Voice Bot', meta: '6 touchpoints', color: '#1b52c4', width: 60 },
                { label: '📞 Personal Phone Call', meta: '6 touchpoints', color: '#0891b2', width: 60 },
                { label: '💬 WhatsApp (Personal)', meta: '6 touchpoints', color: '#198754', width: 60 },
                { label: '📧 Email (1:1)', meta: '4 touchpoints', color: '#f59e0b', width: 40 },
                { label: '🎥 Zoom / Video Meet', meta: '2 · 45m total', color: '#7c3aed', width: 20 },
                { label: '🤝 Physical Meeting', meta: '2 visits', color: '#212529', width: 20 },
            ],
            commitments: {
                thisPeriod: 9, ytd: 34, conversionRate: 56,
                focused: [
                    { icon: '🧑‍🏫', name: 'Mentorship', total: 4, confirmed: 3, followUp: 1 },
                    { icon: '💼', name: 'Recruitment', total: 2, confirmed: 2, followUp: 0 },
                    { icon: '📢', name: 'Admissions & Marketing', total: 2, confirmed: 1, followUp: 1 },
                ],
                notInFocus: [
                    { icon: '🎤', name: 'Academics & Thought Leadership' },
                    { icon: '💰', name: 'Donation' },
                    { icon: '🏅', name: 'Ranking & Accreditation' },
                ],
            },
        },

        // VD-11 Campaign Interactions
        emailCampaigns: [
            { name: 'Celebrating Women', meta: '6 Mar · Engagement', sent: 428, openRate: 67, clicks: 270, vsPrior: '↑ +14%', vsPriorColor: '#0f9d58', external: false },
            { name: 'Share Your Win', meta: '10 Mar', sent: 428, openRate: 53, clicks: 148, vsPrior: '↑ +8%', vsPriorColor: '#0f9d58', external: false },
            { name: 'Flea Market Invite', meta: '20 Mar', sent: 428, openRate: 48, clicks: 197, vsPrior: '= same', vsPriorColor: '#8fa0c0', external: false },
        ],
        whatsapp: {
            broadcasts: 2,
            tiles: [
                { label: 'Delivered', value: '856' },
                { label: 'Read Rate', value: '71%' },
                { label: 'Replied', value: '148' },
                { label: 'Reply %', value: '17%' },
            ],
            comparePrev: '732 delivered · 66% read', compareDelta: '↑ +124 / +5pp',
            perBroadcast: [
                { name: 'Flea Market Invite', readRate: 68, color: '#198754' },
                { name: 'Profile Nudge', readRate: 74, color: '#0891b2' },
            ],
        },

        // VD-12 Event Activity (conditional)
        events: [
            { day: '15', month: 'Mar', name: 'Alumni Flea Market', typeIcon: '🏫', typeLabel: 'Physical', fullDate: '15 Mar 2026', registered: 67, attended: 58, attendancePct: 87, manualData: true },
        ],

        // VD-13 Other Actions Grid
        otherActions: [
            { key: 'logins', icon: '🔓', value: '643', label: 'Platform Logins', delta: '↑ 38%', prev: '466', iconBg: '#ebf2ff', iconColor: '#1b52c4' },
            { key: 'views', icon: '👁️', value: '1,920', label: 'Profile Views', delta: '↑ 12%', prev: '1,714', iconBg: '#cffafe', iconColor: '#0891b2' },
            { key: 'updates', icon: '✏️', value: '94', label: 'Profile Updates', delta: '↑ 24%', prev: '76', iconBg: '#ede9fe', iconColor: '#7c3aed' },
            { key: 'videosWatched', icon: '🎥', value: '312', label: 'Guidance Videos Watched', delta: '↑ 8%', prev: '289', iconBg: '#ebf2ff', iconColor: '#1b52c4', requires: 'student' },
            { key: 'chats', icon: '💬', value: '57', label: 'Student–Alumni Chats', delta: '↑ 19%', prev: '48', iconBg: '#d1fae5', iconColor: '#0f9d58', requires: 'student' },
            { key: 'qna', icon: '❓', value: '43', label: 'Q&A Posts / Answers', delta: '↑ 5%', prev: '41', iconBg: '#fef3c7', iconColor: '#92400e' },
            { key: 'counselling', icon: '🧑‍⚕️', value: '18', label: 'Counselling Sessions', delta: '↑ 2', prev: '16', iconBg: '#ebf2ff', iconColor: '#1b52c4', requires: 'counselling' },
            { key: 'jobs', icon: '💼', value: '11', label: 'Job / Internship Posts', delta: '↑ 3', prev: '8', iconBg: '#cffafe', iconColor: '#0891b2' },
            { key: 'videosUploaded', icon: '🎬', value: '3', label: 'Guidance Videos Uploaded', delta: 'New!', prev: '0', iconBg: '#d1fae5', iconColor: '#0f9d58' },
            { key: 'albums', icon: '🖼️', value: '6', label: 'Photo Albums Added', delta: '= same', prev: '6', iconBg: '#fef3c7', iconColor: '#92400e' },
            { key: 'eventAttendees', icon: '🤝', value: '58', label: 'Event Attendees', delta: '1 event this period', prev: '0', iconBg: 'rgba(10,31,68,.1)', iconColor: '#0a1f44' },
            { key: 'commitments', icon: '💰', value: '9', label: 'Commitments Captured', delta: '↑ 5', prev: '4', iconBg: '#fce7f3', iconColor: '#db2777' },
        ],
    };
}

export type DashboardData = ReturnType<typeof buildDashboard>;
